<script setup lang="ts">
import { onClickOutside, useEventListener, whenever } from '@vueuse/core';
import { computed, ref, watch } from 'vue';
import { useBreakpoints } from '../utils/breakpoints';
import TmlPopup from './TmlPopup.vue';

const props = withDefaults(
  defineProps<{
    open: boolean;
    align?: 'left' | 'center' | 'right';
    verticalAlign?: 'above' | 'below';
    offset?: string;
    maxHeight?: string;
    width?: string;
    as?: 'dropdown' | 'popup';
    teleport?: boolean;
    zIndex?: number;
  }>(),
  {
    align: 'right',
    verticalAlign: 'below',
    offset: '0.5rem',
    maxHeight: '500px',
    width: 'auto',
    as: undefined,
    teleport: false,
    zIndex: undefined,
  },
);

const emit = defineEmits<{
  (event: 'update:open', value: boolean): void;
  (event: 'scroll', e: Event): void;
}>();

const onScrollableScroll = (e: Event) => {
  emit('scroll', e);
};

const target = ref();
const trigger = ref<HTMLElement>();
const breakpoints = useBreakpoints();
const greaterThanSm = breakpoints.greaterOrEqual('sm');
onClickOutside(
  target,
  () => {
    if (props.open && greaterThanSm.value) {
      emit('update:open', false);
    }
  },
  {
    ignore: [
      '.dropdown-click-outside-ignored',
      props.teleport ? '#popup-teleport-anchor' : undefined,
    ].filter((ignoreOption) => ignoreOption),
  },
);

const as = computed(() => {
  if (props.as) {
    return props.as;
  }

  return greaterThanSm.value ? 'dropdown' : 'popup';
});

const optionsOffsetX = ref(0);
const optionsOffsetY = ref(0);
const optionsWidth = ref(0);

const updateOptionsPosition = () => {
  if (!props.teleport || !trigger.value) {
    return;
  }

  const rect = trigger.value.getBoundingClientRect();
  optionsOffsetX.value = rect.x;
  optionsOffsetY.value = rect.y + rect.height + 1;
  optionsWidth.value =
    props.width && props.width.endsWith('px')
      ? Math.min(rect.width, Number(props.width.replace('px', '')))
      : rect.width;
};

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen && props.teleport) {
      updateOptionsPosition();
    }
  },
);

whenever(
  () => props.teleport,
  () => {
    useEventListener(window, 'scroll', updateOptionsPosition, { capture: true });
    useEventListener(window, 'resize', updateOptionsPosition, { capture: true });
  },
  { immediate: true, once: true },
);
</script>

<template>
  <div ref="target" class="relative">
    <div ref="trigger">
      <slot name="trigger" />
    </div>
    <Transition name="fade">
      <Teleport v-if="open && as === 'dropdown' && teleport" to="#popup-teleport-anchor">
        <div
          class="dropdown-container fixed"
          :style="{
            maxHeight,
            top: `${optionsOffsetY}px`,
            left: `${optionsOffsetX}px`,
            width: `${optionsWidth}px`,
            transform: verticalAlign === 'above' ? 'translateY(-100%)' : 'none',
            ...(zIndex !== undefined && { zIndex }),
          }"
        >
          <div
            class="overflow-auto clip-scrollbar w-full"
            :style="{
              maxHeight,
            }"
            @scroll="onScrollableScroll"
          >
            <slot :as="as" />
          </div>
        </div>
      </Teleport>
      <div
        v-else-if="open && as === 'dropdown'"
        class="dropdown-container"
        :class="{
          'right-0': align === 'left',
          'left-1/2 -translate-x-1/2': align === 'center',
          'left-0': align === 'right',
        }"
        :style="{
          top: verticalAlign === 'below' ? `calc(100% + ${offset})` : undefined,
          bottom: verticalAlign === 'above' ? `calc(100% + ${offset})` : undefined,
          width,
          ...(zIndex !== undefined && { zIndex }),
        }"
      >
        <div
          class="overflow-auto clip-scrollbar w-full"
          :style="{
            maxHeight,
          }"
          @scroll="onScrollableScroll"
        >
          <slot :as="as" />
        </div>
      </div>
      <Teleport v-else-if="open && as === 'popup'" to="#popup-teleport-anchor">
        <TmlPopup :max-height="maxHeight" :max-width="width" @close="emit('update:open', false)">
          <div
            class="overflow-auto clip-scrollbar"
            :style="{
              maxHeight,
            }"
            @scroll="onScrollableScroll"
          >
            <slot :as="as" />
          </div>
        </TmlPopup>
      </Teleport>
    </Transition>
  </div>
</template>

<style scoped>
.dropdown-container {
  @apply absolute z-dropdown shadow-lg rounded-lg overflow-hidden;
  @apply bg-[--tml-dropdown-background-color];
  @apply ring-1 ring-[--tml-dropdown-ring-color];
}
</style>
